// OnboardingRecord.tsx — one person's whole record (12 Oct 2026, the makeover), opened from the Onboarding list
// (?lead=<session id>, ?tab=details|chat|history).
//
// The operator, 9 Oct 2026: "Design improvement required for sure"; "their wallet /sof section is important for
// approval, if wallet is holding funds the user is claiming to exchange or what & then Arkhm cheque etc, + 1 click
// wallet tx history opens up"; "size of the fonts, using cards, colors where applicable, reads as sections like SoF
// Passed, KYC passed, Residency/nationality passed/flagged/pennding, tg card with Tg blue color". 12 Oct: "wallet
// funds should also be listed on that screen itself" — what the wallet holds stands in the head, beside the trade.
//
// A SHEET: beside the list from lg (900 px, the list still in view), the whole screen below it. Its head: who they
// are, their standing, the trade and what the wallet holds. Then ONE contact card in its app's colour — a Telegram and
// a WhatsApp card side by side only once the bot links the two. Then the four checks as cards, each taking you to its
// section. Then Details — Wallet & source of funds first, as the operator approves on it; KYC; Residency &
// nationality; what the card said — Chat (every chat of the person, the team's own words as "You") and History.
//
// A READ OF ITS OWN (the screen's rule 2): when it was read is said under its head, read again every minute while it
// is open; an old or failed read says so and keeps what it had.
//
// 14 Oct 2026: what the wallet holds NOW stands in the head ("Read again" reads the record — and the worker the chain —
// again); under the contact card, their phone number; under the four checks, what is still missing and whether the
// bot can still ask for it; in KYC a link to each ID image in the ops group. And the team asks the customer from here
// (12 Oct, the plan's phase 2): "Ask the customer" on each check opens its form (?ask=); each section lists what was
// asked for it — who asked, when it went out, where it stands, the answer to the team's own question.
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Ban, CheckCheck, CircleCheck, CircleX, ClipboardList, Flag, MessageCircle, StickyNote, TriangleAlert, Users, X,
} from 'lucide-react';
import { ApiError } from '../lib/errors';
import { fmtAmount, fmtDate, fmtDay, fmtTime } from '../lib/format';
import { sameAsset } from '../lib/naming';
import { Address, EmptyState, StateLine, type StateTone } from '../components/kit';
import {
  CardButton, Clip, ContactCard, Line, Lines, OutButton, Parts, READ_EVERY_MS, Section, StatusCards, TradeBig, WaitingLine, WalletLineView,
  failMore, failSaid, failWords, openChat, readOf, targetOf, type Read, SEAT,
} from '../components/OnboardingParts';
import {
  ASKABLE, CHECKS, ITEMS, REQUEST_STATE_WORD, askedWords, fmtPhone, heldOf, isLive, liveLine, liveReading, madePartyWords, onboardingApi,
  partyNameTaken, phoneHow, requestLabel, saleCoin, shortfall, walletLine,
  type ArkhamAnswer, type Asked, type CheckKey, type LeadRecord, type PartyLink, type RequestItem, type RequestView, type TimelineKind,
  type WalletLine,
} from '../lib/onboarding';

export type RecordTab = 'details' | 'chat' | 'history';
/** What the list knows that the record does not: the person's standing in its queue, and the warnings its row
 *  carries — shown in the record's head when it is opened from a row. */
export interface RowSummary { standing: { text: string; tone: 'bad' | 'warn' | null } | null; strip: { text: string; tone: 'bad' | 'warn' } | null }

const STATE_WORD: Record<string, string> = { new: 'In the chat with the bot', awaiting_approval: 'Waiting for a decision', kyc_pending: 'KYC pending', done: 'Approved', rejected: 'Rejected' };
const ORIGIN_WORD: Record<string, string> = { self_custody: 'their own wallet (self-custody)', exchange_withdrawal: 'from an exchange' };
const FREQUENCY_WORD: Record<string, string> = { one_off: 'One-off', recurring: 'Recurring' };
const words = (map: Record<string, string>, code: string | null): string | null =>
  (code ? (Object.hasOwn(map, code) ? map[code] ?? code : code.replace(/_/g, ' ')) : null);
const yesNo = (v: boolean | null): ReactNode => (v === null ? <span className="text-tg-hint">Not answered yet</span> : v ? 'Yes' : <span className="font-semibold text-neg">No</span>);

/** One person's record. `close` takes it off the page; `tab` and `setTab` are the address's, and so are `asking` and
 *  `setAsking` — which check's "Ask the customer" form is open (?ask=). */
export function RecordSheet({ id, row, tab, setTab, close, asking = null, setAsking = () => {} }: {
  id: string; row: RowSummary | null; tab: RecordTab; setTab: (t: RecordTab) => void; close: () => void;
  asking?: CheckKey | null; setAsking?: (k: CheckKey | null) => void;
}) {
  // Read again while open, as the lists are: a record left open must not go on saying what was true an hour ago.
  const q = useQuery({ queryKey: ['onboarding', 'lead', id], queryFn: () => onboardingApi.lead(id), refetchInterval: READ_EVERY_MS });
  // A record read once stays when a later read fails — said to be the earlier one, as the lists say theirs.
  const read: Read<LeadRecord> = readOf(q, () => true);
  const sheet = useRef<HTMLElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close]);
  // The record takes the focus when it opens, so a keyboard and a reader start in it.
  useEffect(() => { sheet.current?.focus(); }, [id]);
  const d = read.d;
  const name = d ? d.name ?? d.who ?? `Lead ${id.slice(0, 8)}` : `Lead ${id.slice(0, 8)}`;
  const gone = q.error instanceof ApiError && q.error.code === 'no_such_lead';
  const go = (section: 'funds' | 'kyc' | 'residency') => {
    setTab('details');
    // After the Details tab is on the page.
    setTimeout(() => document.getElementById(`record-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };
  // The team's requests come with a worker that takes them: none sent (an older worker), no "Ask the customer".
  const canAsk = !!d && Array.isArray(d.requests);
  const body = (
    <aside ref={sheet} tabIndex={-1} role="dialog" aria-label={`The record of ${name}`}
      className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-tg-bg outline-none lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[900px] lg:border-l lg:border-stroke lg:shadow-[-24px_0_48px_rgba(0,0,0,0.5)]">
      <div className="space-y-4 px-4 py-4 lg:px-7 lg:py-6">
        <Head d={d} id={id} name={name} row={row} read={read} close={close} reading={q.fetchStatus === 'fetching'} readAgain={() => void q.refetch()} />
        <ReadLines q={q} read={read} gone={gone} retry={() => void q.refetch()} />
        {d && (
          <>
            <ContactCard t={targetOf(d, { sessionId: d.id }, name)} />
            {d.phone && <PhoneLine p={d.phone} />}
            <PartyLine d={d} />
            <StatusCards a={d.assessment ?? null} go={go} ask={canAsk ? (k) => setAsking(asking === k ? null : k) : undefined} asking={asking}>
              <StillMissing d={d} readMs={read.readMs} />
              {canAsk && asking && <AskForm key={asking} d={d} check={asking} close={() => setAsking(null)} />}
            </StatusCards>
            <div role="tablist" aria-label="The record" className="flex gap-6 border-b border-stroke-soft">
              {([['details', 'Details'], ['chat', `Chat · ${d.chat.length}`], ['history', `History · ${d.timeline.length}`]] as const).map(([key, label]) => (
                <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                  className={`-mb-px border-b-2 pb-2.5 text-title ${tab === key ? 'border-brand-gold font-semibold text-tg-text' : 'border-transparent text-tg-hint hover:text-tg-text'}`}>
                  {label}
                </button>
              ))}
            </div>
            {tab === 'chat' ? <Chat d={d} readMs={read.readMs} /> : tab === 'history' ? <History d={d} /> : <Details d={d} readMs={read.readMs} />}
          </>
        )}
      </div>
    </aside>
  );
  // Into the page's body in a browser: on a phone the console's scrolling column would hold a fixed sheet below its
  // top and short of the screen; from the body it covers the screen. On the server (the tests) it renders in place.
  return typeof document !== 'undefined' ? createPortal(body, document.body) : body;
}

/** The record's head: the strip of the row's warnings, the name big, how they are known, their standing (the row's,
 *  else the application's state), when the record was read — and the trade with what the wallet holds beside it: now,
 *  read from its chain when the worker reads it (the operator, 14 Oct 2026: "wallet live balance i need to see"), with
 *  "Read again" — the worker reads the chain again when its reading is over a minute old — else at its check. */
function Head({ d, id, name, row, read, close, reading, readAgain }: {
  d: LeadRecord | undefined; id: string; name: string; row: RowSummary | null; read: Read<LeadRecord>; close: () => void; reading: boolean;
  readAgain: () => void;
}) {
  const a = d?.answers;
  const claim = a ? { side: a.side, asset: a.asset, sizeBand: a.sizeBand, type: a.type ?? null, amountAed: a.amountAed ?? null } : null;
  const facts = a ? { size: a.size, payout: a.payout } : null;
  const sale = saleCoin(claim);
  const chain = d?.wallet?.chain ?? null;
  // The record carries the stated wallet: none is no wallet given yet. A live reading comes first; a Books older than
  // it sends none, and the line is the check's.
  const line: WalletLine | null = d && (d.wallet || liveReading(d.walletLive))
    ? liveLine(d.walletLive, d.wallet, sale, false, read.readMs, chain)
    : d ? { kind: 'words', text: 'No wallet yet', tone: 'muted' } : null;
  // The chain was tried and never answered: no reading to show — said, beside the check's line.
  const neverRead = !!d?.walletLive && d.walletLive.ok === false && d.walletLive.readAt === null;
  const standing = row?.standing ?? (d?.state ? { text: STATE_WORD[d.state] ?? d.state, tone: null } : null);
  const tone = standing?.tone === 'bad' ? 'text-neg' : standing?.tone === 'warn' ? 'text-warn' : 'text-tg-hint';
  const trade = d && (
    <div className="min-w-0 space-y-1.5">
      <TradeBig c={claim} f={facts} usdt={d.wallet?.claimUsdt ?? null} size="record" />
      {line && <WalletLineView line={line} />}
      {neverRead && <p className="text-body text-warn" data-live="never">The chain didn&apos;t answer — no reading yet</p>}
      {/* Only where the worker reads the chain: a Books older than it, or a network it has no reader for, sends none. */}
      {d.walletLive && (
        <button type="button" className="btn-link text-body disabled:opacity-60" disabled={reading} onClick={readAgain} data-read-again=""
          aria-label="Read the record again — the wallet's chain is read again when its reading is over a minute old">
          {reading ? 'Reading…' : 'Read again'}
        </button>
      )}
    </div>
  );
  return (
    <section className="card overflow-hidden p-0" aria-label="Who and what">
      {row?.strip && (
        <p className={`border-b px-5 py-2 text-body font-medium ${row.strip.tone === 'bad' ? 'border-neg/25 bg-neg-bg text-neg' : 'border-warn/25 bg-warn-bg text-warn'}`} data-strip={row.strip.tone}>
          {row.strip.text}
        </p>
      )}
      <div className="flex items-start gap-4 px-5 py-4">
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="break-words text-figure font-semibold leading-tight">{name}</h2>
          {d?.who && d.who !== name && <p className="truncate text-body text-tg-hint" title={d.who}>{d.who}</p>}
          {standing && <p className={`text-body leading-snug ${tone}`} data-standing="">{standing.text}</p>}
          <p className="text-body text-tg-hint">
            {read.readWords ? <>Record read <span className="tabular-nums">{read.readWords}</span>{reading && ' · reading it again…'}</> : `Lead ${id.slice(0, 8)}`}
          </p>
        </div>
        <div className="hidden max-w-[48%] min-w-0 text-right sm:block">{trade}</div>
        <button type="button" onClick={close} aria-label="Close the record (Esc)" className="-mr-2 -mt-1 rounded-md p-1.5 text-tg-hint hover:bg-tg-secondary hover:text-tg-text">
          <X size={20} />
        </button>
      </div>
      {trade && <div className="border-t border-stroke-soft px-5 py-3 sm:hidden">{trade}</div>}
    </section>
  );
}

/** What is wrong with the record's read: not read yet, failed (keeping what it had, said to be the earlier read), no
 *  connection, more than ten minutes old. A read that failed can be read AGAIN from here — "Try again" — whatever the record
 *  holds: the sheet covers the list's Refresh (a full screen on the phone) and "Read again" in the head needs a record already
 *  read, so a first read that failed had no way out but the minute's next read (Astra, 22 Oct 2026). A lead no longer on
 *  record has none: reading it again cannot bring it back. */
function ReadLines({ q, read, gone, retry }: { q: { isError: boolean; error: unknown; fetchStatus: string }; read: Read<LeadRecord>; gone: boolean; retry: () => void }) {
  // ONE StateLine per read (design system, 22 Oct 2026), in a wrapper that keeps the marker the tests read.
  const box = (line: ReactNode, tone: StateTone = 'warn', more?: ReactNode, action?: ReactNode) => (
    <div data-record-note=""><StateLine tone={tone} more={more} action={action}>{line}</StateLine></div>
  );
  const tryAgain = <button type="button" className="btn-link text-meta" onClick={retry}>Try again</button>;
  if (!read.d) {
    if (q.isError) {
      if (gone) return box('This lead is no longer on record.');
      const f = failSaid("Couldn't read the record", q.error);
      return box(f.line, 'warn', f.more, tryAgain);
    }
    if (q.fetchStatus === 'paused') return box('No connection — the record is read when it is back.');
    return box('Reading the record…', 'neutral');
  }
  // The head above says when the record on screen was read; these lines lead with four words at most so that the longest stamp
  // ("06 Oct, 16:00 · 3 d ago", six words) still fits ten. A lead that has gone says what is on screen — the last read — and
  // leaves the stamp to the head.
  if (q.isError) {
    return gone
      ? box('No longer on record — showing the last read.')
      : box(<>Record refresh failed — read {read.readWords}.</>, 'warn', failMore(q.error), tryAgain);
  }
  if (q.fetchStatus === 'paused') return box(<>No connection — record read {read.readWords}.</>, 'warn', 'It is read again as soon as the connection is back.');
  if (!read.fresh) return box(<>Stale record — read {read.readWords}.</>);
  return null;
}

// ── Under the contact card: their phone number ───────────────────────────────

/** Their phone number (the operator, 14 Oct 2026): "+971 50 999 8877 · verified by Telegram" — or typed by them, or
 *  added by the team — with their WhatsApp one tap away; or that they would rather not give one. */
function PhoneLine({ p }: { p: NonNullable<LeadRecord['phone']> }) {
  const how = p.number ? phoneHow(p) : null;
  const wa = p.number ? `https://wa.me/${p.number.slice(1)}` : null;
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 text-body" data-phone={p.number ? (p.verified ? 'verified' : p.via ?? 'unknown') : p.declined ? 'declined' : 'unreadable'}>
      <span className="text-label font-semibold uppercase tracking-wide text-tg-hint">Phone</span>
      {p.number ? (
        <>
          {/* The number and how it came are one run, the dot held to the number: no line starts with "·". */}
          <span className="tabular-nums">
            <span className="whitespace-nowrap font-medium">{fmtPhone(p.number)}</span>
            {how && <>{'\u00a0· '}<span className={p.verified ? 'text-pos' : 'text-tg-hint'}>{how}</span></>}
          </span>
          {wa && (
            <a className="btn-link text-body" href={wa} target="_blank" rel="noopener noreferrer" aria-label={`Open WhatsApp with ${fmtPhone(p.number)}`}
              onClick={(e) => { e.preventDefault(); openChat(wa); }}>
              WhatsApp
            </a>
          )}
        </>
      ) : p.declined
        ? <span className="text-tg-hint">They&apos;d rather not give a phone number</span>
        : <span className="text-tg-hint">A number is on record but can&apos;t be shown.</span>}
    </p>
  );
}

// ── Under the four checks: what is still missing, and asking for it ─────────

/** What is still missing — in the order the bot asks — and, when the chat waits on them for it, the same "Waiting on
 *  them" line as their card, with whether the bot can still write to them. Nothing from a worker older than it. */
function StillMissing({ d, readMs }: { d: LeadRecord; readMs: number }) {
  const missing = d.stillMissing ?? [];
  if (missing.length === 0 && !d.waitingOn) return null;
  // Said once: while the chips name every item the chat waits on them for, the line below does not name them again.
  const waited = d.waitingOn?.items ?? [];
  const chipsSayIt = missing.length > 0 && waited.length > 0 && waited.every((x) => missing.some((m) => m.key === x.key));
  return (
    <div className="space-y-1.5 rounded-xl border border-stroke px-4 py-3" data-still-missing="">
      {missing.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="text-label font-semibold uppercase tracking-wide text-tg-hint">Still missing</span>
          <ul className="flex flex-wrap gap-1.5" aria-label="Still missing">
            {missing.map((m) => <li key={m.key} className="rounded-md bg-warn-bg px-2 py-0.5 text-body text-warn">{m.label}</li>)}
          </ul>
        </div>
      )}
      {d.waitingOn && <WaitingLine w={d.waitingOn} channel={d.channel} readMs={readMs} items={!chipsSayIt} />}
    </div>
  );
}

/** "Ask the customer" for one check (12 Oct 2026, the plan's phase 2): that check's own items and "Something else",
 *  with a note of at most 200 characters, which the bot puts in its own words. An item already waiting for an answer
 *  is not asked twice. Send asks the BFF (POST /api/onboarding/requests) — signed by whoever is signed in — and says
 *  when the bot asks; the record is then read again, so the request stands in its section's list. */
function AskForm({ d, check, close }: { d: LeadRecord; check: CheckKey; close: () => void }) {
  const qc = useQueryClient();
  const name = CHECKS.find((k) => k.key === check)?.name ?? check;
  const waiting = new Set((d.requests ?? []).filter(isLive).map((r) => r.item).filter((x): x is RequestItem => x !== null));
  const offered: RequestItem[] = [...ASKABLE[check], 'other'];
  const [picked, setPicked] = useState<RequestItem[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; tone: 'plain' | 'warn' } | null>(null);
  const other = picked.includes('other');
  const trimmed = note.trim();
  const problem = picked.length === 0 ? 'Choose what to ask for.' : other && !trimmed ? '"Something else" needs a note — what to ask them.' : null;
  const send = async () => {
    if (problem || busy) return;
    setBusy(true);
    setSaid(null);
    try {
      const r: Asked = await onboardingApi.ask(d.id, picked, trimmed || null);
      setSaid(askedWords(r));
      setPicked([]);
      setNote('');
      // The record — and the lists, whose rows carry what was asked — read again.
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    } catch (e) {
      setSaid({ text: `Couldn't ask — ${failWords(e)}`, tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-xl border border-brand-gold/40 bg-panel-2/40 px-4 py-3.5" role="group" aria-label={`Ask the customer — ${name}`} data-ask-form={check}>
      <div className="flex items-start justify-between gap-3">
        <h4 className="text-body font-semibold">Ask the customer — {name}</h4>
        <button type="button" onClick={close} aria-label="Close the form" className="-mr-1.5 -mt-1 rounded-md p-1 text-tg-hint hover:bg-tg-secondary hover:text-tg-text"><X size={18} /></button>
      </div>
      <ul className="grid gap-1.5 sm:grid-cols-2" aria-label="What to ask for">
        {offered.map((item) => {
          const already = waiting.has(item);
          return (
            <li key={item}>
              <label className={`flex items-center gap-2 text-body ${already ? 'text-tg-hint' : ''}`}>
                <input type="checkbox" className="h-4 w-4 accent-brand-gold" disabled={already || busy} checked={picked.includes(item)}
                  onChange={(e) => setPicked((p) => (e.target.checked ? [...p, item] : p.filter((x) => x !== item)))} data-item={item} />
                <span>{ITEMS[item].label}{already && <span className="text-body"> — already asked, waiting</span>}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <label className="block space-y-1">
        <span className="block text-body text-tg-hint">{other ? 'What to ask them (needed for "Something else")' : 'A note for the bot (optional)'}</span>
        <textarea className="input min-h-[64px] w-full resize-y" maxLength={200} rows={2} value={note} disabled={busy}
          onChange={(e) => setNote(e.target.value)} placeholder={other ? 'e.g. the name of their company' : 'Anything the bot should know when it asks'} />
        <span className="block text-right text-meta tabular-nums text-tg-hint">{note.length}/200</span>
      </label>
      <p className="text-body text-tg-hint">The bot asks in its own words, and again whenever they write.</p>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="btn-primary px-4 py-1.5 text-body disabled:opacity-50" disabled={!!problem || busy} onClick={() => void send()}>
          {busy ? 'Asking…' : 'Send'}
        </button>
        <button type="button" className="btn-secondary px-3 py-1.5 text-body" onClick={close}>Cancel</button>
        {said
          ? <span className={`text-body ${said.tone === 'warn' ? 'text-warn' : 'text-pos'}`} data-asked={said.tone}>{said.text}</span>
          : problem && picked.length > 0 && <span className="text-body text-warn">{problem}</span>}
      </div>
    </div>
  );
}

/** What was asked for one section, newest first: what, where it stands (in its colour — answered green; would rather
 *  not say, or no answer after 7 days, amber), who on the team requested it and when, when it went out to them and
 *  when it closed, the team's note, and their answer to the team's own question. */
function RequestTrail({ list }: { list: RequestView[] }) {
  if (list.length === 0) return null;
  const tone = (s: RequestView['state']) => (s === 'answered' ? 'text-pos' : s === 'declined' || s === 'no_answer' ? 'text-warn' : 'text-tg-hint');
  return (
    <div className="space-y-1.5">
      <p className="text-meta font-semibold uppercase tracking-wide text-tg-hint">Asked of the customer</p>
      <ul className="space-y-2" aria-label="Asked of the customer">
        {list.map((r, i) => {
          const when = [
            r.by || r.at ? `requested${r.by ? ` by ${r.by}` : ''}${r.at ? ` · ${fmtDate(r.at)}` : ''}` : null,
            r.askedAt ? `sent ${fmtDate(r.askedAt)}` : null,
            r.closedAt && r.state !== 'open' && r.state !== 'asked' ? `${r.state === 'answered' ? 'answered' : 'closed'} ${fmtDate(r.closedAt)}` : null,
          ].filter(Boolean).join(' · ');
          return (
            <li key={r.id ?? `r:${i}`} className="text-body leading-snug" data-request-state={r.state ?? 'unknown'}>
              <span className="font-semibold">{requestLabel(r)}</span>
              {' · '}<span className={`font-medium ${tone(r.state)}`}>{r.state ? REQUEST_STATE_WORD[r.state] : 'State not known'}</span>
              {/* Its dots held to the part before them (Parts): no line starts with "·". */}
              {when && <span className="block text-body tabular-nums text-tg-hint"><Parts text={when} /></span>}
              {r.note && <span className="block text-body text-tg-hint">The team&apos;s note: “{r.note}”</span>}
              {r.answer && <span className="block text-body">Their answer: “{r.answer}”</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A section's requests: those of its checks (null: none — a contact, the team's own question). */
const trailOf = (d: LeadRecord, checks: Array<CheckKey | null>): RequestView[] => (d.requests ?? []).filter((r) => checks.includes(r.check));

// ── Details ──────────────────────────────────────────────────────────────────

function Details({ d, readMs }: { d: LeadRecord; readMs: number }) {
  const others = trailOf(d, [null]);
  return (
    <div className="space-y-4">
      <WalletFunds d={d} readMs={readMs} />
      <Kyc d={d} />
      <Residency d={d} />
      <FromTheCard d={d} />
      {/* What the team asked that belongs to no check: a contact, the team's own question. */}
      {others.length > 0 && (
        <Section id="record-other" title="Other questions" requests={<RequestTrail list={others} />}>
          <p className="text-body text-tg-hint">Not tied to any check: contacts, and the team&apos;s own questions.</p>
        </Section>
      )}
    </div>
  );
}

/** Arkham's risk band as a chip: red when severe, amber when high, plain otherwise. */
const BAND_CHIP: Record<string, string> = {
  severe: 'bg-neg-bg font-semibold text-neg', high: 'bg-warn-bg font-semibold text-warn', medium: 'bg-panel-2 text-tg-text', low: 'bg-panel-2 text-tg-hint',
};
/** An Arkham answer that is not a verdict, in words: it never reads as clean. */
const ARKHAM_NOT_RUN: Record<string, string> = {
  unscreened: 'Not screened', error: 'The check failed — open Arkham to look', not_enabled: 'Arkham is not switched on',
  budget_exhausted: 'Not checked — the Arkham budget ran out',
};
function Arkham({ a }: { a: ArkhamAnswer | null }) {
  if (!a) return <span className="text-tg-hint">Not checked yet — Arkham is checked when the card goes</span>;
  if (a.status !== 'clean' && a.status !== 'flagged') return <span className="text-warn">{a.status ? ARKHAM_NOT_RUN[a.status] ?? a.status : 'Its answer could not be read'}</span>;
  // Its verdict, who it is and its band on the first line; its labels under them — no line starts with a stray "·".
  return (
    <span className="flex flex-col gap-1.5">
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {a.status === 'flagged' && <span className="rounded-full bg-neg-bg px-2.5 py-0.5 text-meta font-bold text-neg">Flagged</span>}
        <span className="font-semibold">{a.entity ?? 'No entity named'}</span>
        {a.band && <span className={`whitespace-nowrap rounded-md px-2 py-0.5 text-body tabular-nums ${BAND_CHIP[a.band] ?? BAND_CHIP.low}`}>{a.band}{a.score !== null && ` (${a.score})`}</span>}
      </span>
      {a.labels.length > 0 && <span className="flex flex-wrap gap-1.5">{a.labels.map((l) => <span key={l} className="rounded-md bg-panel-2 px-2 py-0.5 text-body text-tg-hint">{l}</span>)}</span>}
      {a.checkedAt && <span className="text-body text-tg-hint">checked {fmtDate(a.checkedAt)}</span>}
    </span>
  );
}
/** The wallet's risk score as KYT words it — "GL 100 · severe", or why there is none. Severe red, high amber. */
function Risk({ w }: { w: NonNullable<LeadRecord['wallet']> }) {
  if (w.band) {
    const tone = w.band === 'severe' ? 'font-semibold text-neg' : w.band === 'high' ? 'font-semibold text-warn' : '';
    // One token, never split: "GL 100 · severe".
    return <span className={`whitespace-nowrap ${tone}`}>{`${w.score !== null ? `GL ${w.score}` : 'GL'} · ${w.band}`}</span>;
  }
  const word: Record<string, string> = { clean: 'Clean · no score', flagged: 'Flagged · no score', unscreened: 'Not scored yet', error: 'The check failed', none: 'Not checked', pending: 'Check pending' };
  return <span className={w.status === 'flagged' || w.status === 'error' ? 'text-warn' : 'text-tg-hint'}>{w.status ? word[w.status] ?? w.status : 'Could not be read'}</span>;
}

/** The approval section: what they claim, what the wallet holds against it, where the rest is, where the money comes
 *  from — then who the wallet is: sanctions, its risk, Arkham, the address — with its transactions and Arkham one
 *  click away. */
function WalletFunds({ d, readMs }: { d: LeadRecord; readMs: number }) {
  const a = d.answers;
  const w = d.wallet;
  const funds = d.assessment?.funds;
  const wallet = d.assessment?.wallet;
  const sale = saleCoin(a);
  const amount = a.amountAed;
  const claimUsdt = w?.claimUsdt ?? null;   // none from a Books older than it
  const short = sale && w ? shortfall(heldOf(w, sale), claimUsdt?.low ?? null) : null;
  // A Books older than the record's links sends none: no buttons, never a crash.
  const links = w?.links ?? { transactions: null, arkham: null };
  const trail = trailOf(d, ['funds', 'wallet']);
  // What it holds now, read from its chain — beside what it held at its check, which the checks were read from.
  const live = liveReading(d.walletLive) ? d.walletLive : null;
  return (
    <Section id="record-funds" title="Wallet & source of funds"
      verdicts={[...(funds ? [{ s: funds.status, of: 'Funds' }] : []), ...(wallet ? [{ s: wallet.status, of: 'Wallet' }] : [])]}
      aside={links.transactions || links.arkham ? <><OutButton href={links.transactions} label="Transactions" /><OutButton href={links.arkham} label="Arkham" /></> : undefined}
      requests={trail.length ? <RequestTrail list={trail} /> : undefined}>
      <div className="space-y-5">
        <Lines>
          <Line k="Claim">
            {a.side ? (
              <span className="font-semibold tabular-nums">
                <span className="whitespace-nowrap">{a.side === 'sell' ? 'Sells' : 'Buys'} {a.asset ?? 'crypto'}</span>
                {amount && <> · <span className="whitespace-nowrap">{`${amount.upTo ? 'up to ' : ''}${fmtAmount(Math.round(amount.low), 'AED')}${amount.high !== amount.low ? `–${fmtAmount(Math.round(amount.high), 'AED')}` : ''} AED`}</span></>}
                {claimUsdt && sale && <> <span className="whitespace-nowrap">≈ {fmtAmount(Math.round(claimUsdt.low), undefined, 0)} {sale}</span></>}
              </span>
            ) : <span className="text-tg-hint">Not said yet</span>}
            {a.size && <span className="block text-body text-tg-hint">in their words: {a.size}</span>}
          </Line>
          {live && (
            <Line k="Holds now">
              <WalletLineView line={liveLine(live, w, sale, false, readMs, w?.chain ?? null)} label={false} />
              {live.source && <span className="block text-body text-tg-hint">read from {live.source}</span>}
            </Line>
          )}
          {w && (
            <Line k={live ? 'At the check' : 'The wallet holds'}>
              {w.holdings && (w.holdings.length > 0 || sale) ? (
                <table className="text-body tabular-nums">
                  <tbody>
                    {/* The coin on sale missing from a list that is there is none of it (KYT's rule): 0, first, in red. */}
                    {sale && !w.holdings.some((h) => sameAsset(h.asset, sale)) && (
                      <tr><td className="pr-4 text-right font-semibold text-neg">0</td><td className="font-semibold text-neg">{sale}</td></tr>
                    )}
                    {w.holdings.map((h) => (
                      <tr key={h.asset}><td className="pr-4 text-right">{fmtAmount(h.amount, h.asset)}</td><td className="text-tg-hint">{h.asset}</td></tr>
                    ))}
                  </tbody>
                </table>
              ) : <WalletLineView line={walletLine(w, sale, false, w.chain)} label={false} />}
            </Line>
          )}
          {w && sale && typeof w.coversClaim === 'boolean' && (
            <Line k="Against the claim">
              {w.coversClaim
                ? <span className="font-semibold text-pos">Covers the claim</span>
                : short
                  ? <span className="font-semibold text-neg"><span className="whitespace-nowrap">{short.pct}% of the claim</span> · <span className="whitespace-nowrap">short by {fmtAmount(Math.round(short.short), undefined, 0)} {sale}</span></span>
                  : <span className="font-semibold text-neg">Short of the claim</span>}
            </Line>
          )}
          {a.fundsNote && <Line k="Where the rest is"><span>“{a.fundsNote}”</span></Line>}
          <Line k="Source of funds">{a.sourceOfFunds ? <Clip text={a.sourceOfFunds} /> : <span className="text-warn">Not answered yet</span>}</Line>
          <Line k="Purpose">{a.purpose ? <Clip text={a.purpose} /> : <span className="text-warn">Not answered yet</span>}</Line>
          <Line k="Own money">{yesNo(a.ownMoney)}</Line>
        </Lines>
        {w ? (
          <div className="border-t border-stroke-soft pt-4">
            <Lines>
              <Line k="Sanctions">
                {w.sanctions === 'listed' ? <span className="font-bold text-neg">LISTED — on a sanctions list</span>
                  : w.sanctions === 'clear' ? 'Not listed' : w.sanctions === 'not_checked' ? <span className="text-warn">Not checked</span> : <span className="text-tg-hint">Not known</span>}
              </Line>
              <Line k="Risk score"><Risk w={w} /></Line>
              <Line k="Arkham"><Arkham a={w.arkham ?? null} /></Line>
              {w.address && <Line k="Address"><Address value={w.address} className="[&_button]:text-body" /></Line>}
              {(w.chain || w.origin) && <Line k="Network">{[w.chain, words(ORIGIN_WORD, w.origin)].filter(Boolean).join(' · ')}</Line>}
              {w.activity && <Line k="Activity">{w.activity}</Line>}
              {w.checkedAt && <Line k="Checked"><span className="tabular-nums">{fmtDate(w.checkedAt)}</span></Line>}
            </Lines>
          </div>
        ) : <p className="border-t border-stroke-soft pt-4 text-body text-warn">No wallet given yet.</p>}
      </div>
    </Section>
  );
}

/** The latest decision on the record — its words, who and when — from its timeline; null when none was made. */
function decisionOf(d: LeadRecord): LeadRecord['timeline'][number] | null {
  for (let i = d.timeline.length - 1; i >= 0; i--) {
    const t = d.timeline[i]!;
    if (t.kind === 'approved' || t.kind === 'rejected') return t;
  }
  return null;
}

function Kyc({ d }: { d: LeadRecord }) {
  const c = d.assessment?.kyc;
  const decision = decisionOf(d);
  const trail = trailOf(d, ['kyc']);
  // Each ID image's message in the ops group (the operator, 14 Oct 2026: "how to find his ID document?") — opened in
  // Telegram, where it stays; a member of the group sees it.
  const ids = d.idImageLinks ?? [];
  return (
    <Section id="record-kyc" title="KYC" verdicts={c ? [{ s: c.status }] : []} requests={trail.length ? <RequestTrail list={trail} /> : undefined}>
      <Lines>
        <Line k="ID images">
          {d.idImages === null ? <span className="text-tg-hint">Not known</span>
            : <span className={d.idImages < 2 ? 'font-semibold text-warn' : ''}>{d.idImages} of 2 — in the ops group</span>}
          {ids.length > 0 && (
            <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5" data-id-links="">
              {/* Each dot rides with the link before it: a wrapped line starts with a link, never with "·". */}
              {ids.map((url, i) => (
                <span key={`${i}:${url}`} className="whitespace-nowrap">
                  <a className="btn-link text-body" href={url} target="_blank" rel="noopener noreferrer" aria-label={`Open ID image ${i + 1} in the ops group`}
                    onClick={(e) => { e.preventDefault(); openChat(url); }}>
                    ID image {i + 1}
                  </a>
                  {i < ids.length - 1 && <span className="text-tg-hint">{'\u00a0·'}</span>}
                </span>
              ))}
            </span>
          )}
        </Line>
        {d.checks.length > 0 && (
          <Line k="ID against the claim">
            <ul className="space-y-1">
              {d.checks.map((x, i) => (
                <li key={i} className="flex gap-2">
                  {x.ok ? <CircleCheck size={17} className="mt-[3px] shrink-0 text-pos" aria-label="passed" /> : <CircleX size={17} className="mt-[3px] shrink-0 text-neg" aria-label="failed" />}
                  <span className={x.ok ? '' : 'text-neg'}>{x.label}{x.note && <span className="text-tg-hint"> — {x.note}</span>}</span>
                </li>
              ))}
            </ul>
          </Line>
        )}
        <Line k="Decision">
          {decision
            ? <span><span className={`font-semibold ${decision.kind === 'rejected' ? 'text-neg' : ''}`}>{decision.kind === 'rejected' ? 'Rejected' : 'Approved'}</span>{decision.by && <> by {decision.by}</>}{decision.at && <> · <span className="whitespace-nowrap tabular-nums">{fmtDate(decision.at)}</span></>}</span>
            : d.state === 'awaiting_approval' ? <span className="font-semibold text-warn">Waiting for your decision</span> : <span className="text-tg-hint">No decision yet</span>}
        </Line>
      </Lines>
    </Section>
  );
}

function Residency({ d }: { d: LeadRecord }) {
  const a = d.answers;
  const c = d.assessment?.residency;
  const trail = trailOf(d, ['residency']);
  return (
    <Section id="record-residency" title="Residency & nationality" verdicts={c ? [{ s: c.status }] : []} requests={trail.length ? <RequestTrail list={trail} /> : undefined}>
      <Lines>
        <Line k="UAE resident">{yesNo(a.resident)}</Line>
        <Line k="In the UAE now">{yesNo(a.inUae)}</Line>
        <Line k="Nationality">{a.nationality ?? <span className="text-tg-hint">Not answered yet</span>}</Line>
        <Line k="Lives in">{a.jurisdiction ?? <span className="text-tg-hint">Not answered yet</span>}</Line>
      </Lines>
    </Section>
  );
}

/** The card and the rest of the record: where the card is, the application's state, what changed since the card,
 *  the card's caveats (its own words — no status colour of theirs, the checks above carry the colour), who they were
 *  seen as before, and the answers no section above has. */
function FromTheCard({ d }: { d: LeadRecord }) {
  const a = d.answers;
  return (
    <Section id="record-card" title="From the card">
      <Lines>
        <Line k="Card">
          {d.cardUrl ? <CardButton url={d.cardUrl} says="Open in the ops group" /> : d.ref ? <>No link — search <span className="font-mono">{d.ref}</span> in the ops group</> : <span className="text-tg-hint">No card yet</span>}
        </Line>
        {d.state && <Line k="Application">{STATE_WORD[d.state] ?? d.state}</Line>}
        {d.caveats.length > 0 && (
          <Line k="Caveats">
            <ul className="space-y-1.5">
              {d.caveats.map((x, i) => <li key={i} className="flex gap-2"><TriangleAlert size={16} className="mt-[3px] shrink-0 text-tg-hint" aria-hidden />{x}</li>)}
            </ul>
          </Line>
        )}
        {d.changed.length > 0 && (
          <Line k="Changed since">
            <ul className="space-y-1">
              {d.changed.map((x) => <li key={x.field}><span className="text-tg-hint">{x.field}:</span> {x.card ?? 'not given'} → <span className="text-warn">{x.now ?? 'not given'}</span></li>)}
            </ul>
          </Line>
        )}
        {d.priorContact.length > 0 && <Line k="Seen before">{d.priorContact.join(' · ')}</Line>}
        {a.frequency && <Line k="How often">{words(FREQUENCY_WORD, a.frequency)}</Line>}
        {a.referredBy && <Line k="Referred by">{a.referredBy}</Line>}
      </Lines>
    </Section>
  );
}

// ── Chat ─────────────────────────────────────────────────────────────────────

const APP_WORD: Record<string, string> = { telegram: 'Telegram', web: 'Website', whatsapp: 'WhatsApp' };
/** The record's own chat's app: its messages carry no app of their own. */
const homeOf = (d: LeadRecord): 'telegram' | 'web' | 'whatsapp' => (d.channel === 'web' ? 'web' : 'telegram');

/** A day's name, counted to the read (rule 2), never the viewer's clock: "Today", "Yesterday", else "05 Oct". */
function dayName(iso: string, readMs: number): string {
  const day = fmtDay(iso, true);
  if (Number.isFinite(readMs)) {
    if (day === fmtDay(readMs, true)) return 'Today';
    if (day === fmtDay(readMs - 86_400_000, true)) return 'Yesterday';
  }
  return fmtDay(iso);
}

/** Every chat of the person as one conversation: the customer on the left; the bot and the team on the right — the
 *  team's own words in teal as "You", the bot muted; a message from another chat says which; a day's first message
 *  under its day. Opens on the newest message. */
function Chat({ d, readMs }: { d: LeadRecord; readMs: number }) {
  const box = useRef<HTMLDivElement>(null);
  const fit = useChatFit(box);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [d.chat.length, fit]);
  if (d.chat.length === 0) return <div className="card"><EmptyState>No messages on record.</EmptyState></div>;
  const home = homeOf(d);
  let last = '';
  return (
    <div ref={box} style={{ maxHeight: fit ?? '64vh' }} className="card overflow-y-auto overscroll-contain px-4 py-4" role="log" aria-label="Every chat, oldest first">
      <ol className="space-y-2">
        {d.chat.map((m, i) => {
          const day = m.at ? dayName(m.at, readMs) : '';
          const sep = day !== '' && day !== last;
          if (day) last = day;
          const mine = m.from !== 'customer';
          const bubble = m.from === 'customer' ? 'bg-panel-2 rounded-bl-md' : m.from === 'team' ? 'bg-brand-teal/15 ring-1 ring-brand-teal/30 rounded-br-md' : 'bg-panel-3/60 text-tg-hint rounded-br-md';
          const elsewhere = m.channel && m.channel !== home ? APP_WORD[m.channel] : null;
          return (
            <li key={i} data-from={m.from}>
              {sep && <p className="my-3 flex items-center gap-3 text-body text-tg-hint"><span className="h-px flex-1 bg-stroke-soft" aria-hidden />{day}<span className="h-px flex-1 bg-stroke-soft" aria-hidden /></p>}
              <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${bubble}`}>
                  {(m.from !== 'customer' || elsewhere) && (
                    <p className="mb-0.5 flex flex-wrap items-center gap-x-2 text-meta">
                      {m.from === 'team' && <span className="font-semibold text-brand-teal">You</span>}
                      {m.from === 'bot' && <span className="font-medium text-tg-hint">Bot</span>}
                      {elsewhere && <span className="rounded bg-panel-3 px-1.5 text-tg-hint">{elsewhere}</span>}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap break-words text-body leading-snug">{m.text}</p>
                  {m.at && <p className="mt-0.5 text-right text-meta tabular-nums text-tg-hint">{fmtTime(m.at)}</p>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** The chat box's height: the room left on the screen under the record's head, so the chat opens on its newest
 *  message with nothing below the fold; with less than 22rem left (a phone) the tabs go to the top and the box takes
 *  the screen — after which 100dvh follows the screen by itself, and the record is never pulled back to the tabs
 *  each time a phone's address bar comes and goes. */
function useChatFit(box: RefObject<HTMLDivElement | null>): string | null {
  const [fit, setFit] = useState<{ maxH: string; toTabs: boolean } | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let screen = false;
    const measure = () => {
      if (screen) return;
      const room = window.innerHeight - el.getBoundingClientRect().top - 24;
      if (room >= 352) {
        const maxH = `${Math.floor(room)}px`;
        setFit((f) => (f?.maxH === maxH ? f : { maxH, toTabs: false }));
        return;
      }
      screen = true;
      const tabs = el.closest('aside')?.querySelector('[role=tablist]');
      setFit({ maxH: `calc(100dvh - ${Math.ceil((tabs?.getBoundingClientRect().height ?? 40) + 56)}px)`, toTabs: true });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [box]);
  useEffect(() => {
    if (fit?.toTabs) box.current?.closest('aside')?.querySelector('[role=tablist]')?.scrollIntoView({ block: 'start' });
  }, [fit, box]);
  return fit?.maxH ?? null;
}

// ── History ──────────────────────────────────────────────────────────────────

const KIND: Record<TimelineKind, { icon: typeof Flag; tone: string }> = {
  card: { icon: ClipboardList, tone: 'text-tg-hint' }, card_failed: { icon: TriangleAlert, tone: 'text-warn' },
  escalated: { icon: Flag, tone: 'text-tg-hint' }, approved: { icon: CircleCheck, tone: 'text-pos' }, rejected: { icon: CircleX, tone: 'text-neg' },
  contacted: { icon: MessageCircle, tone: 'text-tg-hint' }, handled: { icon: CheckCheck, tone: 'text-tg-hint' },
  refused: { icon: Ban, tone: 'text-neg' }, declined: { icon: Ban, tone: 'text-warn' }, note: { icon: StickyNote, tone: 'text-tg-hint' },
};
/** What was done, newest first: an icon for its kind, its words (three lines, the rest a tap away), when and by whom. */
function History({ d }: { d: LeadRecord }) {
  if (d.timeline.length === 0) return <div className="card"><EmptyState>Nothing recorded yet.</EmptyState></div>;
  const items = [...d.timeline].reverse();
  return (
    <ol className="card px-5 py-4" aria-label="What was done, newest first">
      {items.map((t, i) => {
        const k = KIND[t.kind ?? 'note'];
        const Icon = k.icon;
        return (
          <li key={i} className="relative flex gap-3.5 pb-5 last:pb-0" data-kind={t.kind ?? 'note'}>
            {i < items.length - 1 && <span className="absolute bottom-0 left-[15px] top-8 w-px bg-stroke" aria-hidden />}
            <span className="relative flex h-[31px] w-[31px] shrink-0 items-center justify-center rounded-full bg-panel-2 ring-1 ring-stroke"><Icon size={16} className={k.tone} aria-hidden /></span>
            <div className="min-w-0 max-w-[64ch] pt-1">
              <p className="text-pretty text-body leading-snug"><Clip text={t.what} /></p>
              <p className="mt-0.5 text-body tabular-nums text-tg-hint">{t.at ? fmtDate(t.at) : 'time not known'}{t.by && <> · {t.by}</>}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Their party in the Books — the join between the chat bot and the books (D3, the operator, 15 Oct 2026: "yes, build
 *  J1 as a button"). Linked: the party's name, a link to its page. Approved and not yet linked: "Make a party" — the
 *  BFF makes it from the record's name (slug per D4), kyc approved, the Telegram handle kept, and the record reads
 *  again. A party of that name already in the books: said, with the choice — link this customer to it, or make a
 *  second party under a numbered name. Not approved: nothing — a party is made after the decision. A worker that
 *  sends no customer id (older than this) cannot be linked: said, no button. */
function PartyLine({ d }: { d: LeadRecord }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ text: string; tone: 'plain' | 'warn' } | null>(null);
  const [taken, setTaken] = useState<PartyLink | null>(null);
  const approved = d.state === 'done';
  const run = async (how: { link?: string; slug?: string } = {}) => {
    if (busy) return;
    setBusy(true);
    setSaid(null);
    try {
      const r = await onboardingApi.makeParty(d.id, how);
      setTaken(null);
      setSaid(madePartyWords(r));
      void qc.invalidateQueries({ queryKey: ['onboarding'] });
    } catch (e) {
      const clash = partyNameTaken(e);
      if (clash) setTaken(clash.existing);
      else setSaid({ text: `Couldn't make the party — ${failWords(e)}`, tone: 'warn' });
    } finally {
      setBusy(false);
    }
  };
  // A seat like the contact card above it (the operator, 18 Oct 2026: "Make a party button placements and style are not
  // proper"): the Books' gold edge, an icon, the small label, what is there — and the action on the right, the same
  // size and weight as "Open chat". It sits under the phone, so the contact card and the number stay together.
  const seat = (content: ReactNode, action?: ReactNode, extra?: ReactNode) => (
    <div className={`${SEAT} overflow-hidden border-l-4 border-l-brand-gold`} data-party={d.party?.slug ?? ''}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-brand-gold/[0.06] px-4 py-3">
        <Users size={20} className="shrink-0 text-brand-gold" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-label font-semibold uppercase tracking-wide text-brand-gold">Party in the Books</p>
          {content}
          {said && <p className={`text-meta ${said.tone === 'warn' ? 'text-warn' : 'text-tg-hint'}`} role="status">{said.text}</p>}
        </div>
        {action}
        {extra}
      </div>
    </div>
  );
  if (d.party) {
    return seat(
      <Link to={`/parties/${encodeURIComponent(d.party.slug)}?entity=${d.party.entity}`} className="btn-link break-words text-body font-medium" data-party-link="">{d.party.name} →</Link>,
    );
  }
  if (!approved) return null;
  if (!d.customerId) {
    return seat(<p className="text-body leading-snug text-tg-hint">Making a party waits for the bot&apos;s next update.</p>);
  }
  return seat(
    <p className="text-body leading-snug">Not in the Books yet</p>,
    !taken && (
      <button type="button" className="shrink-0 rounded-md bg-brand-gold px-3 py-1.5 text-body font-semibold text-[#0B0F14] hover:brightness-95 disabled:opacity-60"
        disabled={busy} onClick={() => run()} data-make-party="" aria-label="Make a party in the Books from this record">
        {busy ? 'Making…' : 'Make a party'}
      </button>
    ),
    taken && (
      <div className="basis-full space-y-1.5 rounded-lg bg-warn-bg p-3 text-body text-warn" role="status">
        <p>A party named <strong>{taken.name}</strong> is already in the books.</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <button type="button" className="btn-link" disabled={busy} onClick={() => run({ link: taken.slug })}>Link this customer to it</button>
          <button type="button" className="btn-link" disabled={busy} onClick={() => run({ slug: `${taken.slug}-2` })}>Make a second party ({taken.slug}-2)</button>
          <button type="button" className="btn-link" disabled={busy} onClick={() => setTaken(null)}>Cancel</button>
        </div>
      </div>
    ),
  );
}
